// Same as src/pages-new/selfRegistration/selfRegistrationValidation.ts — established
// new-style convention for pre-auth pages (the legacy FirstStep/PatientDigitalEnrollment
// relied on antd's built-in `{type: 'email'}` rule instead).
export const EMAIL_REGEX = /^\w+([+.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/

// Identical to src/pages/patientDigitalEnrollment/PatientDigitalEnrollment.tsx:25 —
// business rule, kept exactly (min 8 chars, at least one lowercase/uppercase/digit/special
// char among !@#$%^&*()).
export const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()]).{8,}$/

// Identical to PatientDigitalEnrollment.tsx:23 — digits only (or empty, required-ness is
// checked separately at the field level, same split as legacy).
export const POSTAL_CODE_REGEX = /^[0-9]*$/

// Identical to PatientDigitalEnrollment.tsx:24.
export const USERNAME_REGEX = /^[a-zA-Z0-9_+\-.!#$'^`~@]+$/

export function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email)
}

export function isValidPassword(password: string): boolean {
  return PASSWORD_REGEX.test(password)
}

export function isValidPostalCode(postalCode: string): boolean {
  return POSTAL_CODE_REGEX.test(postalCode)
}

export function isValidUsername(username: string): boolean {
  return USERNAME_REGEX.test(username)
}

export interface AddressValue {
  country: string
  region: string
  province: string
  city: string
  address: string
  streetNumber: string
  postalCode: string
  locality: string
}

export interface MainDataValue {
  firstName: string
  lastName: string
  sex: string
  birthDate: string | null
  email: string
  cellPhoneNumber: string
  preferredContactMethods: string[]
  username: string
  password: string
  confirmPassword: string
}

export interface CaregiverValue {
  caregiverFirstName: string
  caregiverLastName: string
  caregiverPhoneNumber: string
  caregiverCellPhoneNumber: string
  caregiverEmail: string
}

export interface ConsentOption {
  value: string
  label: string
  required: boolean
}

/**
 * New-style equivalent of the legacy consent Form.Item rules in
 * PatientDigitalEnrollment.tsx:154-164: an optional checkbox (`required: false`) never
 * produces an error regardless of its value (including no selection at all); a required
 * one must be exactly 'CONSENT' — missing and 'NOT_CONSENT' get distinct messages, same as
 * legacy's separate `required` rule vs. custom validator.
 */
export function validateConsentOption(option: ConsentOption, value: string | undefined): string | undefined {
  if (!option.required) return undefined
  if (!value) return 'Questo campo è obbligatorio'
  if (value !== 'CONSENT') return 'Questo consenso è obbligatorio'
  return undefined
}

interface BuildPatchBodyParams {
  mainData: MainDataValue
  address: AddressValue
  caregiver: CaregiverValue
  consentOptions: ConsentOption[]
  consents: Record<string, string>
}

/**
 * New-style equivalent of the legacy `onFinish` in PatientDigitalEnrollment.tsx:121-141.
 * Same submitted shape (confirmPassword/consentPassword never enter the body, to begin
 * with, so there's nothing to delete like the legacy code does).
 */
export function buildPatchBody({mainData, address, caregiver, consentOptions, consents}: BuildPatchBodyParams): Record<string, any> {
  const privacyConsents = consentOptions
    .filter(opt => consents[opt.value] === 'CONSENT')
    .map(opt => opt.value)

  return {
    firstName: mainData.firstName,
    lastName: mainData.lastName,
    sex: mainData.sex,
    birthDate: mainData.birthDate ?? undefined,
    email: mainData.email,
    cellPhoneNumber: mainData.cellPhoneNumber,
    preferredContactMethods: mainData.preferredContactMethods,
    username: mainData.username,
    password: mainData.password,
    country: address.country,
    region: address.region,
    province: address.province,
    city: address.city,
    address: address.address,
    streetNumber: address.streetNumber,
    postalCode: address.postalCode,
    locality: address.locality,
    caregiverFirstName: caregiver.caregiverFirstName,
    caregiverLastName: caregiver.caregiverLastName,
    caregiverPhoneNumber: caregiver.caregiverPhoneNumber,
    caregiverCellPhoneNumber: caregiver.caregiverCellPhoneNumber,
    caregiverEmail: caregiver.caregiverEmail,
    privacyConsents,
  }
}
